import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { useNotification } from '../../context/NotificationContext';
import { getAdminCategories } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import CatalogItemActions from '../../components/CatalogItemActions';
import { removeConfirmMessage, deactivateConfirmMessage } from '../../utils/portalCopy';
import { createCategory, updateCategory, deleteCategory, duplicateCategory } from '../../services/categoryApi';
import ExpandableText from '../../components/ExpandableText';
import {
  trimUrl,
  looksLikeVideoFile,
  googleDriveFileIdAny,
  driveEmbedPreviewUrl,
  driveThumbnailUrl,
  driveViewPageUrl,
  resolveMediaUrlForEmbed,
  resolveUrlForImageLoad,
  adminListThumbSrc,
  normalizeDigitalOceanSpacesCdnUrl,
} from '../../utils/adminMediaUrls';
import MediaUploadInput from '../../components/MediaUploadInput';
import './AdminPages.css';
import usePortalScope from '../../hooks/usePortalScope';

const CategoryManagement = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const portal = usePortalScope();
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    logo: '',
    isActive: true
  });
  const [imagePreviewFailed, setImagePreviewFailed] = useState(false);
  const [logoPreviewFailed, setLogoPreviewFailed] = useState(false);

  const searchKey = useMemo(() => JSON.stringify({ searchTerm }), [searchTerm]);

  const getCategoryParams = useCallback(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    return params;
  }, [searchTerm]);

  const { items: categories, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: getAdminCategories,
    resetKey: searchKey,
    getParams: getCategoryParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(categories, (category) => category._id, {
    defaultView: 'grid',
    resetKey: searchKey
  });

  useEffect(() => {
    setImagePreviewFailed(false);
  }, [formData.image]);

  useEffect(() => {
    setLogoPreviewFailed(false);
  }, [formData.logo]);

  const persistMediaUrl = (raw) => {
    const trimmed = trimUrl(raw);
    if (!trimmed) return '';
    return normalizeDigitalOceanSpacesCdnUrl(resolveMediaUrlForEmbed(trimmed, 'image'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: String(formData.name || '').trim(),
        description: formData.description || '',
        image: persistMediaUrl(formData.image),
        logo: persistMediaUrl(formData.logo),
        isActive: Boolean(formData.isActive)
      };
      if (editingCategory) {
        await updateCategory(editingCategory._id, payload);
      } else {
        await createCategory(payload);
      }
      setShowForm(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', image: '', logo: '', isActive: true });
      showSuccess(editingCategory ? 'Category updated.' : 'Category created.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || `Failed to ${editingCategory ? 'update' : 'create'} category`);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      image: category.image || '',
      logo: category.logo || '',
      isActive: category.isActive !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Remove category',
      message: removeConfirmMessage('category'),
      confirmText: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await deleteCategory(id);
      showSuccess('Category removed.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove category');
    }
  };

  const handleDuplicate = async (category) => {
    try {
      await duplicateCategory(category._id);
      showSuccess('Category duplicated into your catalog.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to duplicate category');
    }
  };

  const handleToggleActive = async (category) => {
    const nextActive = !category.isActive;
    if (!nextActive) {
      const ok = await confirm({
        title: 'Deactivate category',
        message: deactivateConfirmMessage('category'),
        confirmText: 'Deactivate',
        danger: true
      });
      if (!ok) return;
    }
    try {
      await updateCategory(category._id, {
        isActive: nextActive
      });
      showSuccess(nextActive ? 'Category activated.' : 'Category deactivated.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update category');
    }
  };

  const catalogActions = (category) => (
    <CatalogItemActions
      portal={portal}
      item={category}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onDuplicate={handleDuplicate}
      onToggle={handleToggleActive}
      toggleLabel={category.isActive ? 'Deactivate' : 'Activate'}
    />
  );

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '', image: '', logo: '', isActive: true });
  };

  const renderImagePreview = (raw, failed, setFailed) => {
    const v = trimUrl(raw);
    if (!v) return null;
    if (looksLikeVideoFile(v)) {
      return (
        <div className="admin-service-media-hint">
          This URL looks like a video file. Use a static image (PNG, JPG, or WebP) for category image and logo.
        </div>
      );
    }
    const driveId = googleDriveFileIdAny(v);
    if (driveId) {
      return (
        <div className="admin-drive-preview-wrap">
          <img
            className="admin-drive-thumb-inline"
            src={driveThumbnailUrl(driveId)}
            alt=""
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <iframe
            title="Google Drive preview"
            src={driveEmbedPreviewUrl(driveId)}
            className="admin-drive-preview-iframe"
            allow="fullscreen"
          />
          <a
            href={driveViewPageUrl(driveId)}
            target="_blank"
            rel="noopener noreferrer"
            className="admin-service-media-link"
          >
            Open in Google Drive
          </a>
        </div>
      );
    }
    if (failed) {
      const attempted = resolveUrlForImageLoad(raw);
      const isMixedContent =
        typeof window !== 'undefined' &&
        window.location?.protocol === 'https:' &&
        /^http:\/\//i.test(String(attempted || ''));
      return (
        <div className="admin-service-media-hint">
          Could not load image.
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}>
            Preview URL:{' '}
            <code style={{ wordBreak: 'break-all' }}>
              {attempted || '(empty)'}
            </code>
          </div>
          {isMixedContent ? (
            <div style={{ marginTop: 6 }}>
              This looks like mixed content: <code>http://</code> media on an <code>https://</code> admin page is blocked by
              the browser. Use <code>https://</code>.
            </div>
          ) : (
            <div style={{ marginTop: 6 }}>
              If the URL is <code>https://</code> but still fails, it’s usually a <strong>403/permission</strong> issue
              (file not public) or the object doesn’t exist yet. Try opening the URL in a new tab to confirm it loads.
            </div>
          )}
        </div>
      );
    }
    return (
      <img
        key={resolveUrlForImageLoad(raw)}
        src={resolveUrlForImageLoad(raw)}
        alt="Preview"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  };

  const categoryColumns = useMemo(
    () => [
      { key: 'name', label: 'Name', render: (category) => <strong>{category.name}</strong> },
      {
        key: 'media',
        label: 'Image / Logo',
        render: (category) => (
          <div className="admin-card-media-strip" style={{ gap: 8 }}>
            {category.image && !looksLikeVideoFile(category.image) ? (
              <img
                src={adminListThumbSrc(normalizeDigitalOceanSpacesCdnUrl(category.image))}
                alt=""
                className="admin-card-media-thumb"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : null}
            {category.logo && !looksLikeVideoFile(category.logo) ? (
              <img
                src={adminListThumbSrc(normalizeDigitalOceanSpacesCdnUrl(category.logo))}
                alt=""
                className="admin-card-media-thumb"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : null}
            {!category.image && !category.logo ? '-' : null}
          </div>
        )
      },
      {
        key: 'description',
        label: 'Description',
        render: (category) =>
          category.description ? (
            <ExpandableText text={category.description} lines={3} />
          ) : (
            '-'
          )
      },
      {
        key: 'status',
        label: 'Status',
        render: (category) => (
          <span className={`status-badge ${category.isActive ? 'active' : 'inactive'}`}>
            {category.isActive ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (category) => catalogActions(category)
      }
    ],
    [portal]
  );

  const categoryListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && categories.length > 0 && (
        <p className="admin-infinite-end">All categories loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const renderCategoryGridCard = (category) => (
    <div className="admin-card admin-card--stretch">
      {(category.image || category.logo) && (
        <div className="admin-category-card-media">
          <div className="admin-card-media-strip">
            {category.image && !looksLikeVideoFile(category.image) && (
              <div className="admin-category-thumb-col">
                <span className="admin-service-media-label">Image</span>
                <div className="admin-card-media-thumb-wrap">
                  <img
                    src={adminListThumbSrc(normalizeDigitalOceanSpacesCdnUrl(category.image))}
                    alt=""
                    className="admin-card-media-thumb"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}
            {category.logo && !looksLikeVideoFile(category.logo) && (
              <div className="admin-category-thumb-col">
                <span className="admin-service-media-label">Logo</span>
                <div className="admin-card-media-thumb-wrap">
                  <img
                    src={adminListThumbSrc(normalizeDigitalOceanSpacesCdnUrl(category.logo))}
                    alt=""
                    className="admin-card-media-thumb"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="card-header">
        <div>
          <h3>{category.name}</h3>
          {category.description && (
            <p className="card-description">
              <ExpandableText text={category.description} lines={3} />
            </p>
          )}
        </div>
        <div className="card-actions">
          <span className={`status-badge ${category.isActive ? 'active' : 'inactive'}`}>
            {category.isActive ? 'Active' : 'Inactive'}
          </span>
          {catalogActions(category)}
        </div>
      </div>
      {(category.image || category.logo) && (
        <div className="card-details">
          {category.image && (
            <p>
              <strong>Image:</strong>{' '}
              <ExpandableText text={category.image} lines={1} minCharsForToggle={60} />
            </p>
          )}
          {category.logo && (
            <p>
              <strong>Logo:</strong>{' '}
              <ExpandableText text={category.logo} lines={1} minCharsForToggle={60} />
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout
      pageTitle="Category Management"
      subtitle={
        pagination.total
          ? `${categories.length} of ${pagination.total} categories · scroll for more`
          : 'Manage service categories'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <div className="admin-page-header-title-row">
            <h1 style={{ margin: 0 }}>Category List</h1>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search categories by name, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setEditingCategory(null);
              setFormData({ name: '', description: '', image: '', logo: '', isActive: true });
              setShowForm(true);
            }}
          >
            + Add Category
          </button>
        </div>

        {loading && categories.length === 0 ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
        {showForm && (
          <div className="admin-card">
            <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <MediaUploadInput
                    label="Image URL"
                    value={formData.image}
                    onChange={(v) => setFormData({ ...formData, image: v })}
                    accept="image/*"
                    maxSizeMb={2}
                    showInlinePreview={false}
                    placeholder="https://…"
                    helperText="Paste URL, Google Drive link, or drag & drop an image. Share Drive files with Anyone with the link."
                  />
                </div>
                <div className="form-group">
                  <MediaUploadInput
                    label="Logo/Icon URL"
                    value={formData.logo}
                    onChange={(v) => setFormData({ ...formData, logo: v })}
                    accept="image/*"
                    maxSizeMb={2}
                    showInlinePreview={false}
                    placeholder="https://…"
                    helperText="Used on TV category chips. Paste URL, Drive link, or upload an image."
                  />
                </div>
              </div>
              {(trimUrl(formData.image) || trimUrl(formData.logo)) && (
                <div className="admin-service-media-preview-section">
                  <div className="admin-service-media-preview-title">Preview</div>
                  <div className="admin-service-media-preview-row">
                    {trimUrl(formData.image) && (
                      <div className="admin-service-media-preview-block">
                        <span className="admin-service-media-label">Image</span>
                        <div className="admin-service-media-frame admin-service-media-frame--image">
                          {renderImagePreview(formData.image, imagePreviewFailed, setImagePreviewFailed)}
                        </div>
                      </div>
                    )}
                    {trimUrl(formData.logo) && (
                      <div className="admin-service-media-preview-block">
                        <span className="admin-service-media-label">Logo / icon</span>
                        <div className="admin-service-media-frame admin-service-media-frame--image">
                          {renderImagePreview(formData.logo, logoPreviewFailed, setLogoPreviewFailed)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="category-is-active"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  aria-label="Category is active"
                />
                <label htmlFor="category-is-active" style={{ margin: 0, fontWeight: 600, cursor: 'pointer' }}>
                  Active
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

            <AdminListViewControls
              viewMode={listView.viewMode}
              onViewModeChange={listView.setViewMode}
              selectedCount={listView.selectedCount}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              totalCount={categories.length}
            showSelection={false}
            />
            <AdminSelectableList
              items={categories}
              getItemId={(category) => category._id}
              viewMode={listView.viewMode}
            selectable={false}
              isSelected={listView.isSelected}
              onToggleItem={listView.toggleItem}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              columns={categoryColumns}
              renderGridCard={renderCategoryGridCard}
              emptyState={
                <div className="empty-state">
                  <p>No categories found. Create your first category!</p>
                </div>
              }
              scrollRef={scrollRef}
              footer={categoryListFooter}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default CategoryManagement;
